import { useAxios } from 'hooks/useAxios';
import { AxiosErrorHandler } from 'shared/Helpers';
import { BuyerCaddyCompetitor, CommunityType, PeerGroupUsage } from 'models/SupplierDynamics';
import { orderBy } from 'lodash-es';
import { useQuery } from '@tanstack/react-query';
import { BASE_URL, BASE_URL_V2 } from 'shared/constants';

/**
 * Failed vendor details from data refresh job
 */
export interface FailedVendor {
  id: number;
  name: string;
  vendor_domain?: string;
}

/**
 * Pagination information from API response
 */
export interface PaginationInfo {
  pageNumber: number;
  pageSize: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  next: boolean;
}

/**
 * Data refresh job response structure
 */
export interface DataRefreshJob {
  id: string;
  startTime: string;
  completedAt?: string;
  status: string;
  syncType: 'SUPPLIER_IQ' | 'SUPPLIER_RISK' | 'PRODUCT_IQ';
  successfulVendors: number;
  failedVendors: number;
  createdBy: string;
  createdByName: string;
  failedVendorDetails: FailedVendor[];
  completedProducts?: number;
  failedProducts?: number;
}

/**
 * Data refresh jobs API response
 */
export interface DataRefreshJobsResponse {
  pagination: PaginationInfo;
  content: DataRefreshJob[][];
}

/**
 * Job status response for a single job
 */
export interface DataRefreshJobStatusResponse {
  id: string;
  status: string;
  completedAt?: string;
  startTime?: string;
  syncType?: 'SUPPLIER_IQ' | 'SUPPLIER_RISK' | 'PRODUCT_IQ';
  successfulVendors?: number;
  completedVendors?: number;
  totalVendors?: number;
  failedVendors?: number;
  failedVendorDetails?: FailedVendor[];
  createdBy?: string;
  createdByName?: string;
  completedProducts?: number;
  failedProducts?: number;
}

/**
 * A vendor discovered by the AI validation workflow
 */
export interface DiscoveredVendor {
  id?: number;
  rank: number;
  name: string;
  domain: string;
  industry?: string | null;
  description?: string | null;
  sourceUrl?: string | null;
  confidence?: string | null;
}

export interface ValidateVendorData {
  finalVendors: DiscoveredVendor[];
  isValid: boolean;
  isExists: boolean;
}

export interface ValidateVendorResponse {
  data: ValidateVendorData;
}

export interface CreateVendorResponse {
  id: number;
  name: string;
  domain?: string | null;
  curatedBy: 'AI' | 'NPI';
}

/**
 * Profile field mapping returned by the v2 vendor search endpoint
 */
export interface VendorProfileFields {
  code?: string;
  name?: string;
  capacity?: string;
  discount?: string;
  listPrice?: string;
  lineNumber?: string;
  extendedPrice?: string;
  totalLicenseCost?: string;
}

/**
 * Vendor profile entry from the v2 vendor search endpoint
 */
export interface VendorProfile {
  id: number;
  label: string;
  fields: VendorProfileFields;
}

/**
 * Single vendor result from the v2 GET /vendor/search endpoint.
 * The response is a flat array of these objects.
 */
export interface V2VendorSearchResult {
  id: number;
  name: string;
  logoFilename?: string | null;
  logoUrl?: string;
  vendorProfile?: VendorProfile[];
  curatedBy: 'AI' | 'NPI';
}

/**
 * Single vendor result from the v2 GET /vendor/search-multi-mode endpoint.
 */
export interface VendorMultiModeSearchResult {
  id: number;
  name: string;
  salesforceId?: string | null;
  domain?: string | null;
}

/**
 * Candidate vendor returned by sim_key search.
 */
export interface SimKeyMatchCandidate {
  id: number;
  name: string;
  domain: string | null;
  simKey: string;
  confidence: 'exact' | 'high' | 'medium' | 'low';
  distance: number;
}

/**
 * Response payload returned by GET /vendor/search-by-sim-key.
 */
export interface SimKeySearchResponse {
  inputSimKey: string;
  exactMatch: SimKeyMatchCandidate | null;
  fuzzyMatches: SimKeyMatchCandidate[];
}

export const useVendorApi = () => {
  const { axios } = useAxios(BASE_URL);
  const { axios: axiosV2 } = useAxios(BASE_URL_V2);

  const getMyVendors = async () => {
    try {
      const res = await axios.get(`/secure/client/vendors`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Filter vendors by contract count criteria
   * @param params - Filter parameters
   * @param params.operator - Comparison operator: "gte" or "lte"
   * @param params.months - Number of months (converted from Days/Weeks/Years)
   * @param params.contractCount - Minimum/maximum number of contracts
   * @returns Promise resolving to array of vendors matching the criteria
   * @throws Error if the API request fails
   */
  const filterVendorsByContracts = async (params: {
    operator: 'gte' | 'lte';
    months: number;
    contractCount: number;
  }) => {
    try {
      const { operator, months, contractCount } = params;
      const res = await axiosV2.get('/vendor/filter-by-contracts', {
        params: {
          operator,
          months,
          contractCount
        }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      // If response is directly an array (as per user's example)
      if (Array.isArray(res.data)) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to filter vendors by contracts'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendors = async (str: string, reseller?: boolean, limitToVendorDynamics?: boolean) => {
    try {
      const res = await axios.get(
        `/${!reseller ? 'vendor' : 'reseller'}/search?str=${str}${limitToVendorDynamics !== undefined ? `&vdi=${+limitToVendorDynamics}` : ''}`,
        {
          baseURL: BASE_URL_V2
        }
      );
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Select supplier info
   * @param id
   * @returns
   */
  const getVendor = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}`);
      return res.data;
    } catch (e) {
      return e;
    }
  };

  const getVendorCategories = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/categories`);
      return res.data;
    } catch (e) {
      return e;
    }
  };

  const getVendorDealDynamics = async (id: number, category_id?: number) => {
    try {
      const res = await axios.get(
        `/secure/vendor/${id}/dealDynamics${category_id ? `?category_id=${category_id}` : ''}`
      );

      return res.data;
    } catch (e) {
      return e;
    }
  };

  const getVendorDealDynamicsAvg = async (id: number, categoryId?: number) => {
    try {
      const res = await axios.get(
        `/secure/vendor/${id}/dealDynamicsAvg${categoryId ? `?category_id=${categoryId}` : ''}`
      );
      return res.data;
    } catch (e) {
      return e;
    }
  };

  const getVendorDealDynamicNotes = async (
    supplier_id: number,
    deal_dynamic_id: number,
    supplier_category_id?: number
  ) => {
    try {
      const res = await axios.get(`/secure/vendor/${supplier_id}/dealDynamics/notes`, {
        params: {
          deal_dynamic_id: deal_dynamic_id,
          supplier_category_id: supplier_category_id || undefined
        }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorReviewQuestions = async () => {
    try {
      const res = await axios.get(`/secure/vendor/review/questions`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const createVendorReview = async (id: number, values: any) => {
    try {
      const res = await axios.post(`/secure/vendor/${id}/review`, values);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorNotesByType = async (id: number, type: CommunityType) => {
    try {
      const res = await axios.get(`/api/vendor/${id}/notes/${type}`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorOverallRatings = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/ratings/overall`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorQuestionRatings = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/question_ratings`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorGoogleNews = async (name: string) => {
    try {
      const res = await axios.get(`/secure/vendor/${name}/google-news`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getVendorReviews = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/reviews`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateVendorReviewHelpful = async (id: number, reviewId: number) => {
    try {
      const res = await axios.post(`/secure/vendor/${id}/review/${reviewId}/is_helpful`, {
        reaction: 1
      });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getDocuments = async (id: number) => {
    try {
      const res = await axios.get(`/secure/vendor/${id}/documents`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch vendors with a supplier dynamics record.
   * V2 returns a flat array: [{ id, name, supplierCategory, mySupplier }]
   * @param profile_id
   */
  const fetchSDSuppliers = async (profile_id?: number) => {
    try {
      const res = await axiosV2.get('/supplier-dynamics/suppliers', {
        params: { profileId: profile_id }
      });
      if (Array.isArray(res.data)) {
        return res.data;
      }
      return Promise.reject(Error('Failed to fetch suppliers'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch vendors list
   * @param profile_id
   */
  const fetchSDSuppliersList = async (profile_id?: number) => {
    try {
      const res = await axios.get(`/api/supplierDynamics/suppliers`, {
        params: { profile_id }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch vendors supplier dynamics scores
   * @param supplier_id
   */
  const fetchSDSupplierScores = async (supplier_id?: number) => {
    try {
      const res = await axios.get(`/api/supplierDynamics/supplier/${supplier_id}/scores`, {
        params: { supplier_id }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch a vendor's competitors
   * @param id
   * @param supplier_category_id
   */
  const getCompetitors = async (id: number, supplier_category_id?: number) => {
    try {
      const res = await axios.get(`/api/supplier/${id}/competitors`, {
        params: { supplier_category_id: supplier_category_id || null }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Retrieves the competitors of a supplier based on their ID.
   * @async
   * @param {number} id - The ID of the supplier to retrieve competitors for.
   * @returns {Promise<any[] | undefined>} - A Promise that resolves with an array of competitors,
   *                                         or undefined if an error occurs.
   */
  const getSupplierCompetitors = async (id: number): Promise<any[] | undefined> => {
    try {
      const res = await axios.get(`/api/supplierDynamics/supplier/${id}/competitors`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Fetch a vendor's competitors
   * @param id
   */
  const getVendorPricingTrend = async (id: number) => {
    try {
      const res = await axios.get(`/secure/supplier/${id}/pricing/trend`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Update Vendor Logo filename field
   * @param vendor_id
   * @param logo_filename
   */
  const updateSupplierLogoFilename = async (vendor_id: number, logo_filename: string) => {
    try {
      const res = await axios.patch(`/secure/supplier/${vendor_id}/logoFilename`, { logo_filename });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Retrieve a vendor's ESG scores
   * @param vendor_id
   */
  const fetchVendorESGScores = async (vendor_id: number) => {
    try {
      const res = await axios.get(`/api/supplier/${vendor_id}/esg`);
      if (res.data?.status === 'success') {
        return res.data?.data;
      }
      return Promise.reject(Error(res.data?.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Select supplier selections
   * @param take
   */
  const selectSupplierSelections = async (take?: number) => {
    try {
      const res = await axios.get(`/supplier-dynamics/supplier/selections`, { baseURL: BASE_URL_V2, params: { take } });
      if (res?.status === 200) {
        return res?.data;
      }
      return Promise.reject(Error(res?.data?.error?.message || 'Failed to fetch supplier selections'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Select supplier title for SupplierCardTitle
   * @param id
   */
  const selectSupplierTitle = async (id: number) => {
    try {
      const res = await axios.get(`/sd/supplier/${id}/title`);
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier title'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  /**
   * Select My Supplier
   * @param id
   * @param profile_id
   */
  const selectMySupplier = async (id: number, profile_id?: number) => {
    try {
      const res = await axios.get(`/api/supplier/${id}/my`, {
        params: { profile_id }
      });
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      return Promise.reject(Error(res.data.message));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const getPeerGroupUsage = (params: { supplier_id?: number; profile_id?: number }): Promise<Array<PeerGroupUsage>> => {
    const { supplier_id, profile_id } = params;
    const reqUrl = `/api/cohorts/supplier/${supplier_id}/usage`;

    return axios
      .get(reqUrl, { params: { profile_id } })
      .then((res) => orderBy(res?.data?.data, ['percentage'], ['desc']));
  };

  const getClientCompetitors = (params: { profile_id?: number }): Promise<Array<BuyerCaddyCompetitor>> => {
    const { profile_id } = params;
    const reqUrl = `/api/companies/competitors`;

    return axios.get(reqUrl, { params: { profile_id } }).then((res) => res?.data?.data);
  };

  const addCustomRunway = async (supplier_id: number, value: number) => {
    try {
      const res = await axiosV2.post(`/supplier/${supplier_id}/client/custom-runway`, { runway: value });
      if (res && res.status >= 200 && res.status < 300) {
        return { status: 'success' as const, data: res.data };
      }
      return { status: 'error' as const };
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const updateCustomRunway = async (supplier_id: number, value: number) => {
    try {
      const res = await axios.patch(`/api/supplier/${supplier_id}/client/custom-runway`, { runway: value });
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const deleteCustomRunway = async (supplier_id: number) => {
    try {
      const res = await axios.delete(`/api/supplier/${supplier_id}/client/custom-runway`);
      return res.data;
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  const fetchCustomRunway = async (supplier_id?: number) => {
    try {
      const res = await axiosV2.get(`/supplier/${supplier_id}/client/custom-runway`);
      if (res?.status === 200) {
        return res.data ?? null;
      }
      return Promise.reject(Error('Failed to fetch custom runway'));
    } catch (e) {
      AxiosErrorHandler(e);
    }
  };

  // ----- BUYER CADDY / QUANTUM API (Supplier Dynamics Plus Beta) - DISABLED: real impl commented out, stubs return undefined -----
  /**
   * @param _vendor
   * @param _page
   * @param _size
   * @deprecated Buyer Caddy API disabled - no-op stub
   */
  const fetchQuantumDataVendors = async (_vendor?: string, _page = 0, _size = 20) => {
    // try {
    //   const res = await axios.get('/api/vendors/search', { params: { vendor, fuzzy: true, page, size } });
    //   if (res?.data?.status === 'success') return res?.data?.data;
    //   return Promise.reject(Error(res?.data?.message || 'Failed to fetch supplier dynamics plus vendors'));
    // } catch (e) { AxiosErrorHandler(e); }
    return undefined;
  };

  /**
   * Searches vendors via the v2 endpoint GET /vendor/search.
   * Returns a flat array of V2VendorSearchResult including curatedBy.
   * Used when an NPI vendor is selected from the dropdown to determine
   * whether the vendor is curated by AI or NPI.
   * @param {string} [str] - Vendor name search string
   * @returns {Promise<V2VendorSearchResult[]>} Flat array of matched vendors
   */
  const searchVendorsV2 = async (str?: string): Promise<V2VendorSearchResult[]> => {
    try {
      const res = await axiosV2.get('/vendor/search', {
        params: { str }
      });
      if (Array.isArray(res.data)) {
        return res.data as V2VendorSearchResult[];
      }
      if (res?.data?.status === 'success') {
        return res?.data?.data;
      }
      return Promise.reject(Error('Failed to fetch vendors from v2 search'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Searches vendors by sim_key (normalized supplier name).
   * Returns exact match (if any) and ranked fuzzy matches.
   * @param {string} supplierName - Raw supplier name to normalize and search
   * @param supplierDomain
   * @returns {Promise<SimKeySearchResponse>} Exact and fuzzy sim_key matches
   */
  const searchVendorsBySimKey = async (
    supplierName: string,
    supplierDomain?: string | null
  ): Promise<SimKeySearchResponse> => {
    try {
      const res = await axiosV2.get('/vendor/search-by-sim-key', {
        params: { supplierName, ...(supplierDomain ? { supplierDomain } : {}) }
      });
      if (res.data && typeof res.data === 'object') {
        return res.data as SimKeySearchResponse;
      }
      return Promise.reject(Error('Failed to fetch sim_key search results'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  const searchVendorsMultiMode = async (str: string): Promise<VendorMultiModeSearchResult[]> => {
    try {
      const res = await axiosV2.get('/vendor/search-multi-mode', {
        params: { str }
      });
      if (Array.isArray(res.data)) {
        return res.data as VendorMultiModeSearchResult[];
      }
      return Promise.reject(Error('Failed to fetch vendors from multi-mode search'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * @param _vendor_domain
   * @deprecated Buyer Caddy API disabled - no-op stub
   */
  const fetchQuantumVendorDetails = async (_vendor_domain?: string | undefined) => {
    // try {
    //   const res = await axios.get(`/api/vendor/${vendor_domain}/details`, { params: { vendor_domain } });
    //   if (res?.data?.status === 'success') return res?.data?.data;
    //   return Promise?.reject(Error(res?.data?.message || 'Failed to fetch supplier dynamics plus vendor details'));
    // } catch (e) { AxiosErrorHandler(e); }
    return undefined;
  };

  /**
   * @param _vendor_domain
   * @deprecated Buyer Caddy API disabled - no-op stub
   */
  const getQuantumSupplierCompetitors = async (_vendor_domain: string) => {
    // try {
    //   const res = await axios.get(`/api/vendor/competitors`, { params: { vendor_domain } });
    //   if (res?.data?.status === 'success') return res?.data?.data;
    //   return Promise?.reject(Error(res?.data?.message || 'Failed to fetch supplier dynamics plus vendor competitors'));
    // } catch (e) { AxiosErrorHandler(e); }
    return undefined;
  };

  /**
   * @param _vendor_domain
   * @deprecated Buyer Caddy API disabled - no-op stub
   */
  const fetchQuantumProductAlternatives = async (_vendor_domain: string) => {
    // try {
    //   const res = await axios.post('/api/vendor/products/alternatives', { vendor_domain, alternativeProductSize: 9, product_ids: [] });
    //   if (res?.data?.status === 'success') return res?.data?.data;
    //   return Promise.reject(Error(res?.data?.message || 'Failed to fetch product and its alternatives'));
    // } catch (e) { AxiosErrorHandler(e); }
    return undefined;
  };

  /**
   * Starts a vendor data refresh scan for Supplier IQ
   * @param payload - The data refresh payload containing sync type and vendor IDs
   * @param payload.syncType - The type of sync to perform (currently 'SUPPLIER_IQ')
   * @param payload.vendorIds - Array of vendor IDs to scan
   * @returns Promise resolving to the API response data
   * @throws Error if the API request fails
   */
  const startVendorDataRefresh = async (payload: {
    syncType: 'PRODUCT_IQ' | 'SUPPLIER_IQ' | 'SUPPLIER_RISK';
    vendorIds: number[];
  }) => {
    try {
      const resetPayload = {
        syncType: payload.syncType,
        vendorIds: payload.vendorIds
      };
      const res = await axiosV2.post('/vendor/data-refresh/start', resetPayload);
      const isSuccess = res?.data?.status === 'success' || (res?.status && res.status >= 200 && res.status < 300);

      if (isSuccess) {
        return res?.data?.data ?? res?.data;
      }

      return Promise.reject(Error(res?.data?.message || 'Failed to start vendor data refresh'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches the latest status for a specific data refresh job.
   * @param jobId - The ID of the job to poll.
   * @returns Promise resolving to the job's current status.
   * @throws Error if the API request fails
   */
  const getDataRefreshJobStatus = async (jobId: string) => {
    try {
      const res = await axiosV2.get(`/vendor/data-refresh/jobs/${jobId}/status`);
      if (res.data?.status === 'success') {
        return res.data.data;
      }

      if (res.data?.id && res.data?.status) {
        return res.data;
      }

      return Promise.reject(Error(res.data?.message || 'Failed to fetch job status'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches the status for multiple data refresh jobs in batches.
   * Processes IDs in groups of up to 10, sending comma-separated IDs as query parameters.
   * @param jobIds - Array of job IDs to fetch status for
   * @returns Promise resolving to an array of job statuses
   * @throws Error if the API request fails
   */
  const getDataRefreshJobsStatus = async (jobIds: string[]): Promise<DataRefreshJobStatusResponse[]> => {
    try {
      if (!jobIds || jobIds.length === 0) {
        return [];
      }

      // Process IDs in batches of 10
      const batchSize = 10;
      const batches: string[][] = [];
      for (let i = 0; i < jobIds.length; i += batchSize) {
        batches.push(jobIds.slice(i, i + batchSize));
      }

      // Execute all batches in parallel
      const results = await Promise.all(
        batches.map((batch) => {
          const idsParam = batch.join(',');
          return axiosV2.get('/vendor/data-refresh/jobs/status', {
            params: {
              jobIds: idsParam
            }
          });
        })
      );

      // Merge all results
      const allStatuses: DataRefreshJobStatusResponse[] = [];
      for (const res of results) {
        if (res.data?.status === 'success' && Array.isArray(res.data?.data)) {
          allStatuses.push(...res.data.data);
        } else if (Array.isArray(res.data)) {
          // Handle case where data is returned directly as array
          allStatuses.push(...res.data);
        } else if (res.data?.data && Array.isArray(res.data.data)) {
          allStatuses.push(...res.data.data);
        }
      }

      return allStatuses;
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches data refresh jobs with pagination
   * @param params - Query parameters for fetching jobs
   * @param params.page - The page number (1-based), default: 1
   * @param params.size - The number of items per page, default: 10
   * @param params.syncType - The type of synchronization (SUPPLIER_IQ, SUPPLIER_RISK, PRODUCT_IQ)
   * @returns Promise resolving to the paginated jobs data
   * @throws Error if the API request fails
   */
  const getDataRefreshJobs = async (params: {
    page?: number;
    size?: number;
    syncType: 'SUPPLIER_IQ' | 'SUPPLIER_RISK' | 'PRODUCT_IQ';
  }) => {
    try {
      const { page = 1, size = 10, syncType } = params;
      const res = await axiosV2.get('/vendor/data-refresh/jobs', {
        params: {
          page,
          size,
          syncType
        }
      });
      const payload = res.data?.data ?? res.data;

      if (res.data?.status === 'success' && res.data?.data) {
        return res.data.data;
      }

      // Some endpoints return data directly without wrapping in { status, data }
      if (payload?.pagination || payload?.content) {
        return payload;
      }

      return Promise.reject(Error(res.data?.message || 'Failed to fetch data refresh jobs'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Cancels a data refresh job
   * @param jobId - The ID of the job to cancel
   * @returns Promise resolving to the cancellation response
   * @throws Error if the API request fails
   */
  const cancelDataRefreshJob = async (jobId: string) => {
    try {
      const res = await axiosV2.post(`/vendor/data-refresh/jobs/${jobId}/cancel`);
      if (res.data?.status === 'success' || res.status === 200) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to cancel job'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Fetches AI config settings for the current user
   * @returns Promise resolving to the user's AI config settings
   * @throws Error if the API request fails
   */
  const getAiConfigSettings = async () => {
    try {
      const res = await axiosV2.get('/vendor/ai-config/settings');
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      // Handle direct response format
      if (res.data?.operator !== undefined) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to fetch AI config settings'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Saves AI config settings for the current user
   * @param settings - The AI config settings to save
   * @param settings.operator - Filter operator: 'gte' or 'lte'
   * @param settings.contractCount - Number of contracts threshold
   * @param settings.timeValue - Time value for the filter period
   * @param settings.timeUnit - Time unit: 'Days', 'Weeks', 'Months', or 'Years'
   * @returns Promise resolving to the saved settings
   * @throws Error if the API request fails
   */
  const saveAiConfigSettings = async (settings: {
    operator: 'gte' | 'lte';
    contractCount: number;
    timeValue: number;
    timeUnit: 'Days' | 'Weeks' | 'Months' | 'Years';
  }) => {
    try {
      const res = await axiosV2.put('/vendor/ai-config/settings', settings);
      if (res.data?.status === 'success') {
        return res.data.data;
      }
      // Handle direct response format
      if (res.data?.operator !== undefined) {
        return res.data;
      }
      return Promise.reject(Error(res.data?.message || 'Failed to save AI config settings'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Validates a vendor name + domain pair against existing Vantage vendors.
   * @param vendorName - Trimmed vendor name entered by the user
   * @param vendorDomain - Extracted domain (e.g. "acme.com")
   * @returns ValidateVendorResponse with finalVendors, isValid, isExists
   */
  const validateVendor = async (vendorName: string, vendorDomain: string): Promise<ValidateVendorResponse> => {
    try {
      const res = await axiosV2.get('/vendor/validate', {
        params: { vendorName, vendorDomain },
        validateStatus: (status) => (status >= 200 && status < 300) || [400, 404, 409].includes(status)
      });
      if ([400, 404, 409].includes(res.status)) {
        const validationError = new Error(res.data?.message || 'Vendor validation failed') as Error & {
          response?: { status?: number; data?: { message?: string } };
        };
        validationError.response = {
          status: res.status,
          data: res.data
        };
        throw validationError;
      }
      if (res.data?.data) return res.data as ValidateVendorResponse;
      if (res.data?.finalVendors !== undefined) return { data: res.data } as ValidateVendorResponse;
      return Promise.reject(new Error(res.data?.message || 'Vendor validation failed'));
    } catch (e) {
      if ((e as { response?: unknown })?.response) throw e;
      AxiosErrorHandler(e);
      throw e;
    }
  };

  /**
   * Creates a vendor through the governed supplier-management API.
   * @param vendorName - Confirmed vendor name
   * @param vendorDomain - Clean domain (e.g. "acme.com")
   * @returns CreateVendorResponse with id, name, domain, curatedBy
   */
  const createVendor = async (vendorName: string, vendorDomain: string): Promise<CreateVendorResponse> => {
    try {
      const res = await axiosV2.post('/vendor', { vendorName, vendorDomain });
      if (res.data?.id) return res.data as CreateVendorResponse;
      if (res.data?.data?.id) return res.data.data as CreateVendorResponse;
      return Promise.reject(new Error(res.data?.message || 'Vendor creation failed'));
    } catch (e) {
      AxiosErrorHandler(e);
      throw e;
    }
  };

  return {
    getMyVendors,
    filterVendorsByContracts,
    getVendors,
    getVendor,
    getVendorCategories,
    getVendorDealDynamics,
    getVendorDealDynamicsAvg,
    getVendorDealDynamicNotes,
    getVendorReviewQuestions,
    createVendorReview,
    getVendorNotesByType,
    getVendorOverallRatings,
    getVendorQuestionRatings,
    getVendorGoogleNews,
    getVendorReviews,
    updateVendorReviewHelpful,
    getDocuments,
    fetchSDSuppliers,
    fetchSDSuppliersList,
    fetchSDSupplierScores,
    getCompetitors,
    getSupplierCompetitors,
    getVendorPricingTrend,
    updateSupplierLogoFilename,
    fetchVendorESGScores,
    selectSupplierSelections,
    selectSupplierTitle,
    selectMySupplier,
    getPeerGroupUsage,
    getClientCompetitors,
    addCustomRunway,
    updateCustomRunway,
    deleteCustomRunway,
    fetchCustomRunway,
    fetchQuantumDataVendors,
    fetchQuantumVendorDetails,
    getQuantumSupplierCompetitors,
    fetchQuantumProductAlternatives,
    startVendorDataRefresh,
    getDataRefreshJobStatus,
    getDataRefreshJobsStatus,
    getDataRefreshJobs,
    cancelDataRefreshJob,
    getAiConfigSettings,
    saveAiConfigSettings,
    validateVendor,
    createVendor,
    searchVendorsV2,
    searchVendorsBySimKey,
    searchVendorsMultiMode
  };
};

export const usePeerGroupUsage = (params: { supplier_id?: number; profile_id?: number }, config = {}) => {
  const { getPeerGroupUsage } = useVendorApi();
  const queryKey = ['peerGroupUsage', params.supplier_id, params.profile_id].filter(Boolean);

  return useQuery({
    queryKey,
    queryFn: () => getPeerGroupUsage(params),
    ...config
  });
};

export const useClientCompetitors = (params: { profile_id?: number }, config = {}) => {
  const { getClientCompetitors } = useVendorApi();
  const queryKey = ['clientCompetitors', params.profile_id].filter(Boolean);

  return useQuery({
    queryKey,
    queryFn: () => getClientCompetitors(params),
    ...config
  });
};
